import { type ProductDeposit } from "@kanpla/api-contract";

import { type FlexMenu } from "~/api/menus/types";
import { type CreatePreOrdersArgs } from "~/api/orders/requests";

import {
  type VariantChoice,
  type PreOrderLine
} from "../__app.collect.orders.create/orders-create-context";
import { pricePreOrderLine } from "../__app.collect.orders.create/price-pre-order-line";
import { warnOnChoicesMissingFromCatalog } from "../__app.collect.orders.create/variant-deposits";

export function parsePreOrderLines(
  menus: FlexMenu[],
  preOrderLines: PreOrderLine[]
): CreatePreOrdersArgs["basket"] {
  return preOrderLines.flatMap((line) => {
    const module = menus.find((module) => module.moduleId === line.moduleId);
    if (!module) return [];

    const product = module.products.find((product) => product.id === line.id);
    if (!product) return [];

    const category =
      module.categories.find((category) => category.id === product.categoryId)
        ?.name || "";

    warnOnChoicesMissingFromCatalog(
      product.id,
      module.variants,
      line.choices ?? []
    );

    const { variantDeposits } = pricePreOrderLine(
      product,
      module.variants,
      module.discounts,
      quantityByChoiceId(line.choices)
    );

    const options = parseOptions(
      module.variants,
      variantDeposits.byChoiceId,
      line.choices
    );

    return {
      moduleId: line.moduleId,
      category,
      unitPriceExclVat: product.unitPriceExclVat,
      name: product.name,
      id: product.id,
      productLineId: product.productLineId,
      type: options ? "variant" : "regular",
      taxRate: {
        id: product.taxRate.id,
        vatRate: product.taxRate.vatRate,
        name: product.taxRate.name
      },
      ...(options ? { options } : {}),
      ...(product.deposit
        ? {
            deposit: {
              depositReturnSchemeId: product.deposit.depositReturnSchemeId,
              feeExclVat: product.deposit.feeExclVat,
              feeInclVat: product.deposit.feeInclVat
            }
          }
        : {})
    };
  });
}

type BuildOptionsResult = NonNullable<
  CreatePreOrdersArgs["basket"][number]["options"]
>;

function quantityByChoiceId(
  selected: VariantChoice[] = []
): Record<string, number> {
  return selected.reduce<Record<string, number>>((quantities, choice) => {
    const amount = Number(choice.quantity) || 0;
    if (amount <= 0) return quantities;

    quantities[choice.id] = (quantities[choice.id] ?? 0) + amount;
    return quantities;
  }, {});
}

function parseOptions(
  variants: FlexMenu["variants"],
  depositByChoiceId: Record<string, ProductDeposit>,
  selected?: VariantChoice[]
): BuildOptionsResult | undefined {
  if (!variants?.length || !selected?.length) return undefined;

  const variantById = new Map(variants.map((g) => [g.id, g]));

  const qtyByVariantAndChoice = new Map<
    string,
    Record<string | number, number>
  >();

  for (const sel of selected) {
    const amount = Number(sel.quantity) || 0;
    if (amount <= 0) continue;

    if (!qtyByVariantAndChoice.has(sel.variantId)) {
      qtyByVariantAndChoice.set(sel.variantId, {});
    }
    const inner = qtyByVariantAndChoice.get(sel.variantId);
    if (!inner) continue;
    inner[sel.id] = (inner[sel.id] ?? 0) + amount;
  }

  const options: NonNullable<BuildOptionsResult> = [];

  for (const [variantId, choicesMap] of qtyByVariantAndChoice) {
    const variant = variantById.get(variantId);
    if (!variant) continue;

    const choiceById: Record<
      string | number,
      (typeof variant.choices)[number]
    > = {};
    for (const choice of variant.choices) {
      choiceById[choice.id] = choice;
    }
    const choices: NonNullable<BuildOptionsResult>[number]["choices"] = [];

    for (const [choiceId, amount] of Object.entries(choicesMap)) {
      if (amount <= 0) continue;
      const choice = choiceById[choiceId];
      if (!choice) continue;

      const deposit = depositByChoiceId[choice.id];

      choices.push({
        id: choice.id,
        amount,
        name: choice.name,
        unitPriceExclVat: choice.unitPriceExclVat,
        ...(deposit
          ? {
              deposit: {
                depositReturnSchemeId: deposit.depositReturnSchemeId,
                feeExclVat: deposit.feeExclVat,
                feeInclVat: deposit.feeInclVat
              }
            }
          : {})
      });
    }

    if (choices.length > 0) {
      options.push({
        id: variantId,
        choices
      });
    }
  }

  return options.length ? options : undefined;
}
